/**
 * MP Phase 3 — pure parcel mutation helpers for the Zustand store.
 */
import type { SiteEntity } from '../types/site';
import type { SiteBoundaryParcel } from '../types/siteBoundary';
import {
  computeAggregate,
  polygonArea,
  polygonCentroid,
  polygonPerimeter,
} from './siteBoundaryGeometry';

function newParcelId(): string {
  return `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function syncAliases(
  parcels: SiteBoundaryParcel[],
): Pick<
  SiteEntity,
  'siteBoundaries' | 'siteBoundaryAggregate' | 'siteBoundary' | 'siteArea' | 'sitePerimeter' | 'siteCentroid'
> {
  const aggregate = computeAggregate(parcels);
  const first = parcels[0];
  return {
    siteBoundaries: parcels,
    siteBoundaryAggregate: aggregate,
    siteBoundary: first?.polygon,
    siteArea: aggregate.totalArea,
    sitePerimeter: aggregate.totalPerimeter,
    siteCentroid: aggregate.aggregateCentroid,
  };
}

function recomputeParcelGeometry(parcel: SiteBoundaryParcel): SiteBoundaryParcel {
  return {
    ...parcel,
    area: polygonArea(parcel.polygon),
    perimeter: polygonPerimeter(parcel.polygon),
    centroid: polygonCentroid(parcel.polygon),
  };
}

export function addParcelToSite(
  site: SiteEntity,
  polygon: Array<{ lat: number; lng: number }>,
  meta?: Partial<Pick<SiteBoundaryParcel, 'label' | 'intendedUse' | 'ownership' | 'notes'>>,
): Partial<SiteEntity> {
  const parcels = [...(site.siteBoundaries ?? [])];
  const idx = parcels.length;
  const parcel: SiteBoundaryParcel = recomputeParcelGeometry({
    id: newParcelId(),
    label: meta?.label ?? `Parcel ${idx + 1}`,
    polygon,
    area: 0,
    perimeter: 0,
    centroid: polygonCentroid(polygon),
    intendedUse: meta?.intendedUse,
    ownership: meta?.ownership,
    notes: meta?.notes,
  });
  parcels.push(parcel);
  return syncAliases(parcels);
}

export function removeParcelFromSite(site: SiteEntity, parcelId: string): Partial<SiteEntity> | null {
  const parcels = (site.siteBoundaries ?? []).filter(p => p.id !== parcelId);
  if (parcels.length === 0) return null;
  return syncAliases(parcels);
}

export function updateParcelPolygonOnSite(
  site: SiteEntity,
  parcelId: string,
  polygon: Array<{ lat: number; lng: number }>,
): Partial<SiteEntity> {
  const parcels = (site.siteBoundaries ?? []).map(p =>
    p.id === parcelId ? recomputeParcelGeometry({ ...p, polygon }) : p,
  );
  return syncAliases(parcels);
}

export function updateParcelMetadataOnSite(
  site: SiteEntity,
  parcelId: string,
  meta: Partial<Pick<SiteBoundaryParcel, 'label' | 'intendedUse' | 'ownership' | 'notes' | 'parcelId'>>,
): Partial<SiteEntity> {
  const parcels = (site.siteBoundaries ?? []).map(p =>
    p.id === parcelId ? { ...p, ...meta } : p,
  );
  return syncAliases(parcels);
}

export function mergeParcelsOnSite(site: SiteEntity, sourceIds: string[], targetId: string): Partial<SiteEntity> | null {
  const parcels = site.siteBoundaries ?? [];
  const sources = parcels.filter(p => sourceIds.includes(p.id));
  const target = parcels.find(p => p.id === targetId);
  if (!target || sources.length === 0) return null;

  const mergedPolygon = target.polygon;
  const remaining = parcels.filter(p => !sourceIds.includes(p.id) || p.id === targetId);
  const mergedTarget = recomputeParcelGeometry({
    ...target,
    polygon: mergedPolygon,
    label: target.label ?? sources.map(s => s.label).filter(Boolean).join(' + '),
  });
  const next = remaining.map(p => (p.id === targetId ? mergedTarget : p));
  return syncAliases(next);
}

export function splitParcelOnSite(
  site: SiteEntity,
  parcelId: string,
  polygons: Array<Array<{ lat: number; lng: number }>>,
): Partial<SiteEntity> | null {
  const parcels = site.siteBoundaries ?? [];
  const source = parcels.find(p => p.id === parcelId);
  if (!source || polygons.length < 2) return null;

  const baseLabel = source.label ?? 'Parcel';
  const newParcels: SiteBoundaryParcel[] = polygons.map((poly, i) =>
    recomputeParcelGeometry({
      ...source,
      id: i === 0 ? source.id : newParcelId(),
      label: `${baseLabel} ${i + 1}`,
      polygon: poly,
    }),
  );
  const next = [...parcels.filter(p => p.id !== parcelId), ...newParcels];
  return syncAliases(next);
}

/** Build site field updates from a parcel list (for admin revert / import). */
export function siteUpdatesFromParcels(parcels: SiteBoundaryParcel[]): Partial<SiteEntity> {
  return syncAliases(parcels);
}
